// Surface lineups (#990): what the Surfaces page reads of the lineup view the
// server answers (GET .../surfaces/:slug/lineup and every lineup action), and
// the Manage mix form. Pure, so the panels and the page share one reading.
import { PREPACK_SPORTS, kambiSportsFor } from '@/utils/kambiSports'
import { humanizeSport } from './prepackSurfaceModel'
import { LOOKAHEAD_OPTIONS } from './prepackScheduleModel'
import { repeatSubjectKey } from './prepackRepeatsModel'

export const LINEUP_STATUS_LABELS = { live: 'Live', available: 'Available', excluded: 'Excluded', unavailable: 'Unavailable' }

// The surface's game window (#1065): the schedule's "Games starting within"
// options, "Any date" read as no window; the server takes 0 to 720 hours.
export const GAME_WINDOW_OPTIONS = LOOKAHEAD_OPTIONS.map((option) => (option.value === 0 ? { value: 0, label: 'No window' } : option))
export const MAX_GAME_WINDOW_HOURS = 720
// The most sports a mix's percentages name (prepacks.MaxSportShares).
export const MAX_SPORT_SHARES = 20
// A window the options do not list reads as Go's GameWindowLabel reads it in
// the server's note: whole days from a week up, else hours.
export function gameWindowLabel(hours) {
  const value = Number(hours)
  const listed = GAME_WINDOW_OPTIONS.find((option) => option.value === value)
  if (listed) return listed.label
  return value % 24 === 0 && value >= 168 ? `Next ${value / 24} days` : `Next ${value} hours`
}

// Why a pack is not live, in operator words. `subject` names the player,
// team or market at its limit.
export function lineupReasonLabel(status, reason, subject) {
  switch (reason) {
    case 'no_vacancy': return 'Waiting for a position'
    case 'over_limit': return subject === 'its schedule' ? 'Held back: its schedule is at its limit' : `Held back: ${subject || 'a repeat'} is at its limit`
    case 'pin_conflict': return `Pin held back: ${subject || 'a repeat'} is at its limit`
    case 'recipe_off': return 'Schedule off for this surface'
    case 'pack_off': return 'Excluded from this surface'
    case 'hidden': return 'Hidden by the sportsbook'
    case 'expired': return 'Game started'
    case 'invalid': return 'Not valid for publication'
    case 'filtered': return 'Outside this surface’s filters'
    case 'surface_off': return 'Surface disabled'
    case 'outside_window': return 'Waiting for the game window'
    case 'zero_share': return 'Not shown: its sport is at 0% in the mix'
    case 'allocation_full': return 'Waiting: its sport’s allocation is full'
    default: return LINEUP_STATUS_LABELS[status] || ''
  }
}

// Waiting packs (the game window holds them back) are counted apart from
// the other unavailable packs, which cannot show at all.
export function lineupCountsLabel(counts = {}) {
  const broken = Math.max(0, (counts.unavailable ?? 0) - (counts.waiting ?? 0))
  return `${counts.displayed ?? 0} displayed · ${counts.available ?? 0} available${counts.held > 0 ? ` · ${counts.held} held back` : ''}${counts.waiting > 0 ? ` · ${counts.waiting} waiting for the game window` : ''}${broken > 0 ? ` · ${broken} unavailable` : ''}`
}

// All books (#1091): the Surfaces page's view of every regulation at once.
// Each book's lineup is read on its own (what delivery serves there) and the
// views are merged here, so no status or position is invented.
export const ALL_BOOKS = 'all'

const sumCounts = (into, counts = {}) => {
  for (const [key, value] of Object.entries(counts)) {
    if (typeof value === 'number') into[key] = (into[key] || 0) + value
  }
  return into
}

/**
 * The lineup views of several books as one view of the same shape, for the
 * page, the panels and the dialogs: slots in position order, each book's
 * first (`book` and `book_label` on every slot, pack and pool entry), counts
 * summed, schedules merged by id, Repeats subjects merged by key, and each
 * book's notes under its name. Each book keeps its own sport allocation in
 * `books` (#1087); the top-level `allocation` is the first book's.
 * books: [{ regulation, label, view }]; a book that has not answered has no
 * view. Null until one has.
 */
export function mergeLineupViews(books) {
  const answered = books.filter((book) => book.view && Array.isArray(book.view.slots))
  if (answered.length === 0) return null
  const first = answered[0].view
  const tag = (book, entry) => ({ ...entry, book: book.regulation, book_label: book.label })
  const order = new Map(answered.map((book, index) => [book.regulation, index]))
  // A pack is in one book: a second answer naming it is not a second card.
  const seenSlots = new Set()
  const slots = answered.flatMap((book) => book.view.slots.map((slot) => tag(book, slot)))
    .filter((slot) => !seenSlots.has(slot.prepack_id) && seenSlots.add(slot.prepack_id))
    .sort((a, b) => a.position - b.position || order.get(a.book) - order.get(b.book))
  const seenPacks = new Set()
  const packs = answered.flatMap((book) => (book.view.packs || []).map((entry) => tag(book, entry)))
    .filter((entry) => !seenPacks.has(entry.prepack_id) && seenPacks.add(entry.prepack_id))
  const recipes = new Map()
  for (const book of answered) {
    for (const recipe of book.view.recipes || []) {
      const merged = recipes.get(recipe.recipe_id)
      if (!merged) {
        recipes.set(recipe.recipe_id, { ...recipe, counts: { ...(recipe.counts || {}) }, pack_ids: [...(recipe.pack_ids || [])] })
        continue
      }
      merged.counts = sumCounts(merged.counts, recipe.counts)
      merged.pack_ids = [...new Set([...merged.pack_ids, ...(recipe.pack_ids || [])])]
      merged.deleted = Boolean(merged.deleted || recipe.deleted)
      merged.targets_surface = Boolean(merged.targets_surface || recipe.targets_surface)
    }
  }
  const repeats = new Map()
  for (const book of answered) {
    for (const subject of book.view.repeats || []) {
      const key = repeatSubjectKey(subject)
      const merged = repeats.get(key)
      if (!merged) {
        repeats.set(key, { ...subject, pack_ids: [...(subject.pack_ids || [])] })
        continue
      }
      merged.packs += subject.packs || 0
      merged.shown += subject.shown || 0
      merged.held += subject.held || 0
      merged.pack_ids = [...new Set([...merged.pack_ids, ...(subject.pack_ids || [])])]
    }
  }
  const notes = answered.flatMap((book) => ((book.view.counts?.total ?? 0) === 0
    ? [{ code: 'no_packs', book: book.regulation, message: `${book.label}: no packs on this ${book.view.context_key ? 'page' : 'surface'}` }]
    : (book.view.notes || []).map((note) => ({ ...note, book: book.regulation, message: `${book.label}: ${note.message}` }))))
  return {
    ...first,
    regulation: ALL_BOOKS,
    slots,
    packs,
    recipes: [...recipes.values()],
    repeats: [...repeats.values()].sort((a, b) => b.packs - a.packs),
    counts: answered.reduce((into, book) => sumCounts(into, book.view.counts), {}),
    unfilled: answered.reduce((sum, book) => sum + (book.view.unfilled || 0), 0),
    notes,
    sports: [...new Set(answered.flatMap((book) => book.view.sports || []))],
    // Moves when any book's lineup does, so the Manage mix projection follows.
    version: answered.reduce((sum, book) => sum + (book.view.version || 0), 0),
    books: answered.map((book) => ({ regulation: book.regulation, label: book.label, displayed: book.view.slots.length, counts: book.view.counts || {}, allocation: book.view.allocation || {} })),
  }
}

/** The books an All books view holds: "3 books", or "2 of 3 books" while one has not answered. */
export function lineupBooksLabel(answered, total = answered) {
  if (answered < total) return `${answered} of ${total} books`
  return `${answered} ${answered === 1 ? 'book' : 'books'}`
}

/**
 * The participants a pack names, once each, as its rows print them. A
 * search passes Infinity, so a participant past the printed three is found.
 */
export function packParticipantSummary(pack, limit = 3) {
  const names = []
  for (const leg of pack?.legs || []) {
    const name = String(leg?.participant || leg?.selection_name || '').trim()
    if (name && !names.includes(name)) names.push(name)
  }
  const shown = names.slice(0, limit).join(' · ')
  return names.length > limit ? `${shown} +${names.length - limit}` : shown
}

/**
 * The lineup view joined with the page's packs: live slots in position order
 * and the available pool, each with its pack; ids the page does not hold (a
 * pack published a moment ago) are left out until its packs refresh.
 */
export function lineupFromView(view, packsById) {
  if (!view || !Array.isArray(view.slots)) return null
  const statusById = new Map((view.packs || []).map((entry) => [entry.prepack_id, entry]))
  const slots = view.slots
    .map((slot) => ({ ...slot, pack: packsById.get(slot.prepack_id) }))
    .filter((slot) => slot.pack)
  const available = (view.packs || [])
    .filter((entry) => entry.status === 'available' && packsById.has(entry.prepack_id))
    .map((entry) => ({ ...entry, pack: packsById.get(entry.prepack_id) }))
  return {
    slots,
    available,
    statusById,
    counts: view.counts || {},
    unfilled: view.unfilled || 0,
    notes: view.notes || [],
    mode: view.mode || 'balanced',
    displayCount: view.display_count || slots.length,
    recipes: view.recipes || [],
    sports: view.sports || [],
    limits: view.limits || {},
    targets: view.targets || {},
    // A Balanced mix's sport percentages as they came out (#1087), per
    // share sport; empty when none apply to this lineup.
    allocation: view.allocation || {},
    repeats: Array.isArray(view.repeats) ? view.repeats : [],
    version: view.version || 0,
    // All books (#1091): the books merged into this view, else null.
    books: Array.isArray(view.books) ? view.books : null,
  }
}

const matches = (text, query) => String(text || '').toLowerCase().includes(query)

// The most packs one switch request names (the server's limit); a larger
// All on / All off goes in batches.
export const MAX_PACK_SWITCHES = 500

/**
 * The Connected schedules rows: each schedule (and the manually added packs)
 * with its pack rows, narrowed by a search over schedule names, pack titles
 * and participants. A schedule whose name matches keeps every pack; one
 * found by its packs keeps only the packs that match, so All on / All off
 * switch only what the row shows.
 */
export function recipeRows(view, packsById, search = '') {
  if (!view) return []
  const query = search.trim().toLowerCase()
  const statusById = new Map((view.packs || []).map((entry) => [entry.prepack_id, entry]))
  return (view.recipes || []).flatMap((recipe) => {
    const packs = (recipe.pack_ids || [])
      .map((id) => packRow(packsById.get(id), statusById.get(id)))
      .filter(Boolean)
    if (!query || matches(recipe.name, query)) return [{ ...recipe, packs }]
    const found = packs.filter((pack) => packRowMatches(pack, query))
    return found.length ? [{ ...recipe, packs: found }] : []
  })
}

function packRow(pack, entry) {
  if (!entry) return null
  return {
    id: entry.prepack_id,
    title: pack?.title || 'Pack',
    summary: packParticipantSummary(pack),
    participants: packParticipantSummary(pack, Infinity),
    odds: pack?.combinedOdds ?? pack?.odds ?? '—',
    status: entry.status,
    reason: entry.reason || '',
    subject: entry.subject || '',
    enabled: entry.enabled !== false,
    pinned: Boolean(entry.pinned),
    position: entry.position || null,
    book: entry.book_label || '',
    pack,
  }
}

function packRowMatches(row, query) {
  return matches(row.title, query) || matches(row.participants, query)
}

/** A recipe's pack rows narrowed by the search inside it. */
export function filterPackRows(rows, search = '') {
  const query = search.trim().toLowerCase()
  return query ? rows.filter((row) => packRowMatches(row, query)) : rows
}

// The Manage mix form. A blank limit inherits the organisation's repeat
// limit; 0 is no limit (#990 D5).
const LIMIT_FIELDS = [['maxPerPlayer', 'max_per_player'], ['maxPerTeam', 'max_per_team'], ['maxPerMarket', 'max_per_market'], ['maxPerRecipe', 'max_per_recipe']]

export function mixDefaults(surface) {
  const mix = surface?.layout_config?.mix || {}
  const form = {
    mode: mix.mode === 'custom' ? 'custom' : 'balanced',
    allowRepeats: mix.allow_repeats !== false,
    sportTargets: { ...(mix.sport_targets || {}) },
    avoidAdjacentMarkets: mix.avoid_adjacent_markets !== false,
    keepPins: mix.keep_pins !== false,
    gameWindowHours: Number(mix.game_window_hours) > 0 ? String(Math.round(Number(mix.game_window_hours))) : '0',
    // Balanced's sport percentages (#1087), as the inputs hold them: the
    // largest share first, then by key, so the rows read the same each load.
    sportShares: Object.fromEntries(
      Object.entries(mix.sport_shares || {})
        .sort(([a, x], [b, y]) => Number(y) - Number(x) || a.localeCompare(b))
        .map(([sport, share]) => [sport, String(share)]),
    ),
  }
  for (const [field, key] of LIMIT_FIELDS) form[field] = mix[key] == null ? '' : String(mix[key])
  return form
}

const round2 = (value) => Math.round(value * 100) / 100
const shareValue = (raw) => {
  const text = String(raw ?? '').trim()
  return text === '' ? 0 : Number(text)
}

/** The panel's form as the API's { mix }. */
export function mixFromForm(form) {
  const mix = { mode: form.mode, allow_repeats: form.allowRepeats !== false, sport_targets: {}, avoid_adjacent_markets: Boolean(form.avoidAdjacentMarkets), keep_pins: Boolean(form.keepPins) }
  for (const [sport, target] of Object.entries(form.sportTargets || {})) {
    const value = Number(target)
    if (sport && Number.isFinite(value) && value >= 0) mix.sport_targets[sport] = Math.round(value)
  }
  for (const [field, key] of LIMIT_FIELDS) {
    const raw = String(form[field] ?? '').trim()
    if (raw !== '' && Number.isFinite(Number(raw))) mix[key] = Math.max(0, Math.round(Number(raw)))
  }
  // Absent when 0, so a mix saved before the window and a form at "No
  // window" are the same mix (sameMix).
  const hours = Number(form.gameWindowHours)
  if (Number.isFinite(hours) && hours > 0) mix.game_window_hours = Math.round(hours)
  // Absent without rows, so a mix saved without percentages and an empty
  // form are the same mix; sorted, so the order rows were added in is not
  // a change.
  const shares = Object.keys(form.sportShares || {}).sort()
  if (shares.length > 0) {
    mix.sport_shares = {}
    for (const sport of shares) {
      const value = shareValue(form.sportShares[sport])
      mix.sport_shares[sport] = Number.isFinite(value) ? round2(value) : 0
    }
  }
  return { mix }
}

/** The form's sport percentages added up, two decimals. */
export function mixSharesTotal(form) {
  return round2(Object.values(form?.sportShares || {}).reduce((sum, value) => sum + (Number.isFinite(shareValue(value)) ? shareValue(value) : 0), 0))
}

const twoDecimals = (value) => Math.abs(value * 100 - Math.round(value * 100)) < 1e-6

export function mixTargetsTotal(form) {
  return Object.values(form?.sportTargets || {}).reduce((sum, value) => sum + (Number(value) || 0), 0)
}

/** Why the form cannot be saved, or null. */
export function mixFormError(form) {
  for (const [field] of LIMIT_FIELDS) {
    const raw = String(form[field] ?? '').trim()
    if (raw !== '' && (!Number.isInteger(Number(raw)) || Number(raw) < 0 || Number(raw) > (field === 'maxPerRecipe' ? 50 : 20))) {
      return field === 'maxPerRecipe' ? 'Packs per schedule must be 0 (no limit) to 50' : 'Packs per player, team or market must be 0 (no limit) to 20'
    }
  }
  // Each target as the API takes it; their total may pass the display
  // count (mixFormWarning).
  for (const [sport, target] of Object.entries(form.sportTargets || {})) {
    const raw = String(target ?? '').trim()
    if (raw === '' || !Number.isInteger(Number(raw)) || Number(raw) < 0 || Number(raw) > 50) {
      return `${sportLabel(sport)} target must be a whole number from 0 to 50`
    }
  }
  const hours = String(form.gameWindowHours ?? '0').trim()
  if (hours === '' || !Number.isInteger(Number(hours)) || Number(hours) < 0 || Number(hours) > MAX_GAME_WINDOW_HOURS) {
    return `Games starting within must be 0 (no window) to ${MAX_GAME_WINDOW_HOURS} hours`
  }
  // Sport percentages (#1087), as the API takes them: each 0 to 100 with two
  // decimals at most, together exactly 100. Kept and checked in Custom too,
  // where they wait unused.
  const shares = Object.entries(form.sportShares || {})
  if (shares.length > MAX_SPORT_SHARES) return `Choose at most ${MAX_SPORT_SHARES} sports`
  if (shares.length > 0) {
    for (const [sport, raw] of shares) {
      const value = shareValue(raw)
      if (!Number.isFinite(value) || value < 0 || value > 100 || !twoDecimals(value)) {
        return `${shareSportLabel(sport)} percentage must be 0 to 100 with at most two decimals`
      }
    }
    const total = mixSharesTotal(form)
    if (Math.abs(total - 100) > 0.005) return `Sport percentages must total 100% (now ${total}%)`
  }
  return null
}

/**
 * What the operator should know before saving, or null: targets are
 * targets, so a total past the display count saves, and the lineup meets
 * what it can.
 */
export function mixFormWarning(form, displayCount) {
  const display = Number(displayCount)
  if (form.mode === 'custom' && Number.isInteger(display) && mixTargetsTotal(form) > display) {
    return `Sport targets add up to ${mixTargetsTotal(form)}, more than the ${display} packs displayed.`
  }
  return null
}

export function sameMix(a, b) {
  return JSON.stringify(mixFromForm(a)) === JSON.stringify(mixFromForm(b))
}

export const sportLabel = (sport) => humanizeSport(sport) || sport

// Sport percentages (#1087) are keyed by the server's sport keys
// (prepacks.SportKey) plus "other", every pack outside the named sports.
export const OTHER_SPORT = 'other'
export const SHARE_SPORT_LABELS = { american_football: 'American football', football: 'Soccer', hockey: 'Ice hockey', [OTHER_SPORT]: 'Other / major events' }
export const shareSportLabel = (sport) => SHARE_SPORT_LABELS[sport] || sportLabel(sport)

const SHARE_SPORT_KEYS = {
  americanfootball: 'american_football', nfl: 'american_football', cfl: 'american_football', ncaaf: 'american_football', ncaa_football: 'american_football',
  mlb: 'baseball',
  nba: 'basketball', wnba: 'basketball', ncaab: 'basketball',
  ice_hockey: 'hockey', nhl: 'hockey',
  martial_arts: 'mma', mixed_martial_arts: 'mma', ufc: 'mma', ufc_mma: 'mma',
  rugby_union: 'rugby', rugby_league: 'rugby',
  motorsport: 'motorsports', motor_sports: 'motorsports', motor_racing: 'motorsports', formula_1: 'motorsports', formula_one: 'motorsports', formula1: 'motorsports', f1: 'motorsports', nascar: 'motorsports', indy_car: 'motorsports', motorcycling: 'motorsports',
  soccer: 'football',
}

/** A lineup view's sport as a share key, the spellings the server's SportKey reads. */
export function shareSportKeyOf(sport) {
  const clean = String(sport || '').trim().toLowerCase().replace(/[-\s]/g, '_')
  return SHARE_SPORT_KEYS[clean] || clean
}

/**
 * Every sport a Balanced mix may name (#1087), as share keys, in the order
 * the Prepacks sport table lists them: the Kambi sports with a builder sport
 * in kambiSports.json, through shareSportKeyOf. A sport with no eligible
 * packs yet may still be given its percentage.
 */
export const SHARE_SPORT_CATALOG = Object.freeze([...new Set(PREPACK_SPORTS.flatMap((sport) => kambiSportsFor(sport).map((entry) => shareSportKeyOf(entry.id))))].filter(Boolean))

export const ALLOCATION_STATUS_LABELS = { met: 'Met', short: 'Short', unavailable: 'No packs', excluded: '0%' }

/**
 * The view's "Packs by sport" rows (#1087): each share sport with its saved
 * and adjusted percentage, its target and filled positions, its eligible
 * packs and status, largest share first.
 */
export function allocationRows(view) {
  return Object.entries(view?.allocation || {})
    .map(([key, entry]) => ({ key, label: shareSportLabel(key), share: entry.share ?? 0, adjusted: entry.adjusted ?? 0, target: entry.target ?? 0, filled: entry.filled ?? 0, available: entry.available ?? 0, status: entry.status || '' }))
    .sort((a, b) => b.share - a.share || a.key.localeCompare(b.key))
}

/** The toast after Show now (#1040), from the view's `promotion`. */
export function promotionMessage(view) {
  const p = view?.promotion
  if (!p) return null
  const landed = `${p.title} is now at #${p.position}.`
  return p.bumped ? `${landed} ${p.bumped.title} went back to the available pool.` : landed
}

/** The conflict a 409 from a pin or replace carries, or null. */
export function lineupConflict(error) {
  return error?.response?.status === 409 ? (error.response.data?.conflict || null) : null
}

// A lineup read that timed out (#1142): the client's own bound (axios's
// ECONNABORTED, or ETIMEDOUT) or the route's 504 (its request budget).
const timedOut = (error) => error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT' || error?.response?.status === 504

/**
 * Whether a failed lineup read is tried again (#1142): as often as the app's
 * other reads are (`retries`, the query client's default count: 1 in
 * Studio), but never after a timeout, which would only wait as long again
 * before the page could say why. TanStack Query's `retry` arguments first;
 * failureCount is 0 on the first failure.
 */
export function retryLineupRead(failureCount, error, retries = 1) {
  return !timedOut(error) && failureCount < Number(retries)
}

/**
 * Why a lineup read failed, in operator words (#1142), from the request's
 * error: a client timeout or the route's 504 is "the read took too long"; a
 * 403 names access; otherwise the server's own message (`error`, else
 * `message`, without a closing period: the callers add theirs), or that it
 * did not answer at all (a network error). Never axios's own message, which
 * is not operator text.
 */
export function lineupFailureReason(error) {
  const status = error?.response?.status
  if (timedOut(error)) return 'the read took too long'
  if (status === 403) return 'you don’t have access to this surface’s lineup'
  const body = error?.response?.data
  const message = [body?.error, body?.message].find((value) => typeof value === 'string' && value.trim())
  if (message) return message.trim().replace(/\.$/, '')
  return 'the server did not answer'
}
